import { IConcreteDesignImperial } from '@/modules/design/interfaces/newConcrete';

export const aashto = function (esal: number, zr: number, std_error: number, delta_psi: number, mr_sub: number): number {
  // console.log(esal, zr, std_error, delta_psi, mr_sub);

  let count = 0;
  let sn = 4;
  let sn_comp = 1;
  const act_esal = Math.log(esal) / Math.log(10);
  let dif = 0;

  if (esal < 11) {
    sn = 0;
    sn_comp = 0;
    dif = 0;
  }

  while (Math.abs(sn_comp) > 0.01 && count < 100) {
    count++;
    const sn_old = sn;
    const p_one = zr * std_error + 9.36 * (Math.log(sn + 1) / Math.log(10)) - 0.2;
    const p_two = Math.log(delta_psi / 2.7) / Math.log(10);
    const p_three = 0.4 + (1094 / ((sn + 1) ** 5.19));
    const p_four = 2.32 * (Math.log(mr_sub) / Math.log(10)) - 8.07;
    const est_esal = p_one + p_two / p_three + p_four;
    dif = est_esal - act_esal;
    if (dif > 1) {
      sn -= 0.5;
    } else if (dif > 0.5) {
      sn -= 0.25;
    } else if (dif > 0.1) {
      sn -= 0.05;
    } else if (dif > 0.01) {
      sn -= 0.005;
    } else if (dif < -1) {
      sn += 0.5;
    } else if (dif < -0.5) {
      sn += 0.25;
    } else if (dif < -0.05) {
      sn += 0.1;
    } else if (dif < -0.01) {
      sn += 0.005;
    }

    sn_comp = sn - sn_old;
  }
  return sn;
};

export const rigidAashto = function (scenario: IConcreteDesignImperial,
                                     esal: number,
                                     zr: number,
                                     std_error: number,
                                     delta_psi: number,
                                     pt: number,
                                     sc: number,
                                     cd: number,
                                     j: number,
                                     ec: number,
                                     k: number):
  {
    WPrime: number,
    D: number,
    sigma_t: number,
    log_b: number,
    sigma_l: number,
    F: number,
    TD: number,
    l: number,
    details: boolean
  } {
  const allowedLoops = 100;
  Math.log10 = function (val) {
    return Math.log(val) / Math.LN10;
  };

  let D = 15;
  const L1 = 18;
  const L2 = 1;
  const meanWindSpeed = scenario.meanWindSpeed_mph || 9;
  const meanTemp = scenario.meanTemp_degF || 2;
  const meanPrecip = scenario.meanPrecip_in || 12;
  const baseModulus = scenario.baseModulus_psi || 1250000;
  const poissonsRatio = scenario.poissonsRatio || 0.20;
  const baseThickness = scenario.baseThickness_in || 6;
  const elasticModulus = scenario.elasticModulus_psi || 4000000;
  const initialServiceability = scenario.initialServiceability || 4.5;
  const terminalServiceability = scenario.terminalServiceability || 3.0;
  const subgradeReactionModulus = scenario.subgradeReactionModulus_psiPerIn || 124.853179210456;
  const slabCoefficient = scenario.slabFrictionCoefficient || 6;
  const jointSpacing = scenario.jointSpacing_in || 120;
  const edgeSupportFactor = scenario.edgeSupport || 0.92;
  const ruptureModulus = sc;// scenario.ruptureModulus || 650;
  const standardError = std_error; // scenario.standardError || 0.39;
  const ZR = zr; // findZr(scenario.reliabilityLevel || 95);
  const d = 10;
  const d_comp = 1;


  interface O {
    [key: number]: any
  }

  const outputTable: O = {};

  while (D >= 6) {
    const d_old = D;

    const ell = CalculateEll(4200000, D, 0.2, 110);
    const ell_prime = CalculateEll(elasticModulus, D, poissonsRatio, subgradeReactionModulus);

    const sigma_part_1 = 0.155 * Math.sqrt(baseThickness * Math.pow((baseModulus / elasticModulus), 0.75));
    const sigma_part_2 = 0.0015 * Math.sqrt((baseModulus * baseThickness) / (1.4 * subgradeReactionModulus));
    const sigma_part_3 = 2.381 * Math.pow(180 / ell, 0.2);

    // Not-prime intermediate vars
    const F = CalculateF(D, 25000, slabCoefficient);
    const logB = CalculateLogB(D, 180, 110, 25000, 6, ell);
    const TD = 0.962 - (52.181 / D) + (0.341 * meanWindSpeed) + (0.184 * meanTemp) - (0.00836 * meanPrecip);
    const sigma_l = CalculateSigmaL(6, 25000, 4200000, 110, ell, D);
    const sigma_t = sigma_l * 1.0 * F * (1 + Math.pow(10, logB) * TD);

    // Prime intermediate vars
    const F_prime = CalculateF(D, baseModulus, slabCoefficient);
    const logB_prime = CalculateLogB(D, jointSpacing, subgradeReactionModulus, baseModulus, baseThickness, ell_prime);
    const TD_prime = 0.962 - (52.181 / D) + (0.341 * meanWindSpeed) + (0.184 * meanTemp) - (0.00836 * meanPrecip);
    const sigma_l_prime = CalculateSigmaL(baseThickness, baseModulus, elasticModulus, subgradeReactionModulus, ell_prime, D);
    const sigma_t_prime = sigma_l_prime * edgeSupportFactor * F_prime * (1 + Math.pow(10, logB_prime) * TD_prime);

    // console.log("F, F'", F, F_prime);
    // console.log("Td", TD, TD_prime);
    // console.log("LogB", logB, logB_prime);

    const G = Math.log10((initialServiceability - terminalServiceability) / (initialServiceability - 1.5));
    const Y = 1 + (3.63 * Math.pow(L1 + L2, 5.2)) / (Math.pow((D + 1), 8.46) * Math.pow(L2, 3.52));

    const log_R = 5.85 + 7.35 * Math.log10(D + 1) - 4.62 * Math.log10(L1 + L2) + 3.28 * Math.log10(L2);
    const log_W = log_R + G / Y;

    const part1 = (5.065 - 0.03295 * Math.pow(terminalServiceability, 2.4));
    const part2 = Math.log10(ruptureModulus / sigma_t_prime);
    const part3 = Math.log10(690 / sigma_t);

    const log_W_prime = log_W + part1 * (part2 - part3);

    const power = (log_W_prime + ZR * standardError);

    const WPrime = Math.pow(10, power);

    const results = {
      WPrime,
      D,
      sigma_t,
      log_b: logB,
      sigma_l,
      F,
      TD,
      l: ell,
      details: true,
    };

    outputTable[Math.round(D * 100)] = results;

    D -= 0.1;
  }


  // Iterates through table to find first configuration that provides sufficient lifetime ESALs
  for (let i = 600; i <= 1500; i += 10) {
    if (scenario.totalDesignEsals && outputTable[i].WPrime > scenario.totalDesignEsals) {
      return outputTable[i];
    }
  }

  // No solution was sufficient, output max thickness
  if (scenario.totalDesignEsals && outputTable[1500].WPrime < scenario.totalDesignEsals) {
    return {
      WPrime: -1,
      D: 15.1,
      sigma_t: -1,
      log_b: -1,
      sigma_l: -1,
      F: -1,
      TD: -1,
      l: -1,
      details: true,
    };
  }

  // It's all messed up, probably shouldn't be able to make it this far
  return {
    WPrime: -1,
    D: -1,
    sigma_t: -1,
    log_b: -1,
    sigma_l: -1,
    F: -1,
    TD: -1,
    l: -1,
    details: true,
  };
};


export const CalculateEll = function (elasticModulus: number,
                                      depth: number,
                                      poissonsRatio: number,
                                      subgradeReactionModulus: number): number {
  const num = (elasticModulus * Math.pow(depth, 3));
  const den = (12 * (1 - Math.pow(poissonsRatio, 2)) * subgradeReactionModulus);
  const result = Math.pow((num / den), 0.25);
  return result;
};

export const CalculateSigmaL = function (baseThickness: number,
                                         baseModulus: number,
                                         elasticModulus: number,
                                         subgradeReactionModulus: number,
                                         L: number,
                                         D: number): number {
  const sigma_part_1 = 0.155 * Math.sqrt(baseThickness * Math.pow((baseModulus / elasticModulus), 0.75));
  const sigma_part_2 = 0.0015 * Math.sqrt((baseModulus * baseThickness) / (1.4 * subgradeReactionModulus));
  const sigma_part_3 = 2.381 * Math.pow(180 / L, 0.2);
  const sigma_l = (18000 / Math.pow(D, 2)) * (4.227 - sigma_part_3 - sigma_part_2 - sigma_part_1);
  return sigma_l;
};

export const CalculateLogB = function (D: number,
                                       jointSpacing: number,
                                       subgradeReactionModulus: number,
                                       baseModulus: number,
                                       baseThickness: number,
                                       L: number): number {
  return -1.944
    + 2.279 * (D / L)
    + 0.0917 * (jointSpacing / L)
    - 433080 * (Math.pow(D, 2) / (subgradeReactionModulus * Math.pow(L, 4)))
    + (0.0614 / L) * Math.sqrt((baseModulus * Math.pow(baseThickness, 1.5)) / (1.4 * subgradeReactionModulus))
    - 438.642 * (Math.pow(D, 2) / subgradeReactionModulus / Math.pow(L, 2))
    - 498240 * (Math.pow(D, 3) * jointSpacing / subgradeReactionModulus / Math.pow(L, 6));
};

export const CalculateF = function (D: number, baseModulus: number, slabCoefficient: number): number {
  const F = 1.177 - 4.3 * Math.pow(10, -8) * D * baseModulus - 0.01155542 * D + 6.27 * Math.pow(10, -7) * baseModulus - (0.000315 * slabCoefficient);
  return F;
};


// Reliability Level
export const findZr = function (reliability: number): number {
  interface M {
    [key: number]: number
  }

  const mapping: M = {
    50: 0.0,
    60: -0.253,
    70: -0.524,
    75: -0.674,
    80: -0.841,
    85: -1.036433,
    90: -1.282,
    95: -1.645,
    99: -2.327,
    99.9: -3.090,
  };

  return mapping[reliability];
};


export const findEp = function (d: number,
                                p: number,
                                a: number,
                                D: number,
                                M: number,
                                minEp: number,
                                maxEp: number,
                                loops: number): { ep: number, SNeff: number } {
  // for (const i in arguments) {
  //   if (isNaN((arguments[i]))) throw ('invalid number for argument ' + arguments[i]);
  // }

  // console.log(d, p, a, D, M, minEp, maxEp, loops);

  const ep = (maxEp + minEp) / 2;
  // const maxEp = 3000000;
  // const minEp = 25000;
  if (loops > 50) {
    return { ep, SNeff: getSNeffFromEp(D, ep) };
  }

  if (ep > maxEp) return { ep: maxEp, SNeff: getSNeffFromEp(D, maxEp) };
  if (ep < minEp) return { ep: minEp, SNeff: getSNeffFromEp(D, minEp) };

  const x = Math.pow(D / a * Math.pow(ep / M, 1 / 3), 2);
  const y = (1 - (1 / Math.sqrt(1 + Math.pow(D / a, 2))));
  const part1 = 1 / (M * Math.sqrt(1 + x));
  const part2 = y / ep;
  const result = 1.5 * p * a * (part1 + part2);
  const diff = Math.abs(100 * (1 - d / result));

  if (d < result && diff > 1) {
    return findEp(d, p, a, D, M, ep, maxEp, loops + 1);
  }
  if (d > result && diff > 1) {
    return findEp(d, p, a, D, M, minEp, ep, loops + 1);
  }

  return { ep, SNeff: getSNeffFromEp(D, ep) };
};

export const getSNeffFromEp = function (D: number, ep: number) {
  for (const i in arguments) {
    if (isNaN((arguments[i]))) throw (`invalid number for argument ${arguments[i]}`);
  }

  return 0.0045 * D * Math.pow(ep, 1 / 3);
};
